import {COUNTRY_CODE, COUNTRY_CODE_LIST} from "@/constant/Constants";

/**
 * 电话号码合法性校验方法
 * 支持手机号、座机号、海外手机号校验
 * @param {string} phone - 待校验的电话号码（本地部分）
 * @param {string} countryCode - 国际区号，默认'+86'
 * @param {boolean} validTel - 是否允许座机号，默认false
 * @returns {object} 校验结果对象
 */
export const validatePhone = (phone, countryCode = '+86', validTel = false) => {
    // 默认返回值
    const defaultResult = {
        status: 0,
        message: 'OK',
        messageType: '',
        isHtml: false
    };

    const errorResult = (message, messageType = '', isHtml = false) => ({
        status: -1,
        message,
        messageType,
        isHtml
    });

    // 基本空值检查
    if (!phone || typeof phone !== 'string') {
        return errorResult('电话号码不能为空');
    }

    const trimmedPhone = phone.trim();

    // 根据validTel参数决定允许的字符
    let allowedCharsRegex;
    if (validTel) {
        // 允许数字和中划线
        allowedCharsRegex = /^[\d\-]*$/;
    } else {
        // 只允许数字
        allowedCharsRegex = /^\d*$/;
    }

    if (!allowedCharsRegex.test(trimmedPhone)) {
        return errorResult(validTel ?
            '电话号码只能包含数字和中划线' :
            '电话号码只能包含数字');
    }

    // 提取纯数字（用于进一步校验）
    const digitsOnly = trimmedPhone.replace(/\D/g, '');

    // 长度检查
    if (digitsOnly.length < 5) {
        return errorResult('电话号码长度过短，至少需要5位数字');
    }

    if (digitsOnly.length > 20) {
        return errorResult('电话号码长度过长，不能超过20位数字');
    }

    // 根据国家代码进行不同的校验
    if (countryCode === '+86') {
        // 中国号码校验
        return validateChinesePhone(trimmedPhone, digitsOnly, validTel, errorResult);
    } else {
        // 国际号码校验
        return validateInternationalPhoneByCode(trimmedPhone, digitsOnly, countryCode, errorResult);
    }
};

/**
 * 中国号码校验（包含手机号和座机号）
 */
const validateChinesePhone = (original, digits, validTel, errorResult) => {
    // 检查是否以0开头（座机号）
    if (original.startsWith('0')) {
        if (!validTel) {
            return errorResult('不允许输入座机号，请使用手机号格式');
        }
        return validateLandlinePhone(original, digits, errorResult);
    }
    // 检查是否是中国手机号
    else if (/^1[3-9]/.test(digits) && digits.length === 11) {
        return validateChineseMobilePhone(digits, errorResult);
    }
    // 其他格式
    else {
        if (validTel) {
            return errorResult('请输入正确的手机号或座机号格式');
        } else {
            return errorResult('请输入正确的11位手机号');
        }
    }
};

/**
 * 根据国家代码校验国际号码
 */
const validateInternationalPhoneByCode = (original, digits, countryCode, errorResult) => {
    // 从国家代码中提取数字部分（去掉+号）
    const countryCodeDigits = countryCode.replace('+', '');

    // 验证国家代码是否在预定义列表中
    if (!COUNTRY_CODE_LIST.includes(countryCode)) {
        return errorResult(
            `国家代码 ${countryCode} 暂不支持，请联系管理员添加`,
            'dialog',
            false
        );
    }

    // 根据国家代码确定本地号码长度规则
    const lengthRules = getPhoneLengthRules(countryCodeDigits);
    const phoneLength = digits.length;

    if (phoneLength < lengthRules.min || phoneLength > lengthRules.max) {
        return errorResult(
            `该国家号码长度应为${lengthRules.min}-${lengthRules.max}位（当前${phoneLength}位）`
        );
    }

    // 特定国家格式检查
    switch (countryCodeDigits) {
        case '1': // 美国/加拿大
            if (phoneLength !== 10) {
                return errorResult('美国/加拿大号码应为10位数字');
            }
            break;

        case '44': // 英国
            if (phoneLength < 9 || phoneLength > 10) {
                return errorResult('英国号码应为9-10位数字');
            }
            break;

        case '81': // 日本
            if (phoneLength !== 10 && phoneLength !== 11) {
                return errorResult('日本号码应为10-11位数字');
            }
            break;

        case '86': // 中国（虽然不会进入这个分支，但保留）
            if (phoneLength !== 11) {
                return errorResult('中国号码应为11位数字');
            }
            break;

        default:
            // 其他国家的通用校验
            if (phoneLength < 6 || phoneLength > 15) {
                return errorResult(`国际号码长度应为6-15位（当前${phoneLength}位）`);
            }
    }

    return {
        status: 0,
        message: 'OK',
        messageType: '',
        isHtml: false
    };
};


/**
 * 获取不同国家的电话号码长度规则
 */
const getPhoneLengthRules = (countryCodeDigits) => {
    const rules = {
        '1': { min: 10, max: 10 },    // 美国/加拿大
        '44': { min: 9, max: 10 },    // 英国
        '86': { min: 11, max: 11 },   // 中国
        '81': { min: 10, max: 11 },   // 日本
        '82': { min: 9, max: 10 },    // 韩国
        '33': { min: 9, max: 9 },     // 法国
        '49': { min: 10, max: 11 },   // 德国
        '7': { min: 10, max: 10 },    // 俄罗斯
        '91': { min: 10, max: 10 },   // 印度
        '61': { min: 9, max: 9 },     // 澳大利亚
        '55': { min: 10, max: 11 },   // 巴西
        '34': { min: 9, max: 9 },     // 西班牙
        '39': { min: 9, max: 10 },    // 意大利
    };

    return rules[countryCodeDigits] || { min: 6, max: 15 }; // 默认规则
};

/**
 * 中国大陆手机号校验（保持不变）
 */
const validateChineseMobilePhone = (digits, errorResult) => {
    // 中国大陆手机号严格正则（最新号段）
    const mobileRegex = /^1(3[0-9]|4[014-9]|5[0-35-9]|6[2567]|7[0-8]|8[0-9]|9[0-35-9])\d{8}$/;

    if (!mobileRegex.test(digits)) {
        // 详细错误信息
        if (digits.length !== 11) {
            return errorResult('中国大陆手机号必须是11位数字');
        }

        if (!/^1[3-9]/.test(digits)) {
            return errorResult('手机号必须以13-19开头');
        }

        const prefix = digits.substring(0, 3);
        const invalidPrefixes = [
            '140', '141', '142', '143', '144', '145', '146', '147', '148', '149', // 物联网号段
            '174' // 卫星通信
        ];

        if (invalidPrefixes.includes(prefix)) {
            return errorResult(`号段 ${prefix} 为物联网或特殊通信号段，不能作为普通手机号使用`);
        }

        return errorResult('手机号格式错误，请检查号码是否正确');
    }

    return {
        status: 0,
        message: 'OK',
        messageType: '',
        isHtml: false
    };
};

/**
 * 座机号校验（稍作调整）
 */
const validateLandlinePhone = (original, digits, errorResult) => {
    // 座机号格式：区号(3-4位) + 号码(7-8位)
    if (digits.length < 10 || digits.length > 12) {
        return errorResult(`座机号格式错误，应为10-12位数字（当前${digits.length}位）`);
    }

    // 区号校验（3位或4位）
    let areaCode, mainNumber;

    if (digits.startsWith('01') || digits.startsWith('02')) {
        // 3位区号（01x或02x）
        if (digits.length !== 11 && digits.length !== 12) {
            return errorResult('3位区号的座机号应为11-12位数字');
        }
        areaCode = digits.substring(0, 3);
        mainNumber = digits.substring(3);
    } else {
        // 4位区号（03xx及以上）
        if (digits.length !== 12) {
            return errorResult('4位区号的座机号应为12位数字');
        }
        areaCode = digits.substring(0, 4);
        mainNumber = digits.substring(4);
    }

    // 区号有效性检查（可以使用您的AREA_CODE.CODE_LIST）
    const validAreaCodes = [
        '010', '020', '021', '022', '023', '024', '025', '027', '028', '029', // 直辖市和省会
        '0310', '0311', '0312', '0313', '0314', '0315', '0316', '0317', '0318', '0319', // 河北
        // 这里可以继续添加其他有效区号...
    ];

    if (!/^0[1-9]/.test(areaCode)) {
        return errorResult(`区号格式错误：${areaCode}`);
    }

    // 主号码校验（7-8位）
    if (mainNumber.length < 7 || mainNumber.length > 8) {
        return errorResult('座机号主体部分应为7-8位数字');
    }

    // 检查分机号格式（如果有）
    if (original.includes('转') || (original.includes('-') && original.split('-').length > 2)) {
        const parts = original.split(/[转\-]/);
        if (parts.length > 2) {
            return errorResult('分机号格式错误，只能有一个分机号');
        }

        const extension = parts[1].replace(/\D/g, '');
        if (extension.length > 6) {
            return errorResult('分机号过长，不能超过6位');
        }
    }

    return {
        status: 0,
        message: 'OK',
        messageType: '',
        isHtml: false
    };
};

export default {
    validatePhone
};
